import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Gstr1Tab } from "@/components/compliance/Gstr1Tab";
import { Gstr3bTab } from "@/components/compliance/Gstr3bTab";
import { Gstr2bTab } from "@/components/compliance/Gstr2bTab";
import { ItcTab } from "@/components/compliance/ItcTab";
import { FilingsTab } from "@/components/compliance/FilingsTab";
import { AuditTab } from "@/components/compliance/AuditTab";
import { api, apiError } from "@/lib/api";
import { downloadApiFile } from "@/lib/download";
import { money, thisMonth } from "@/lib/format";
import { ShieldCheck } from "@phosphor-icons/react";

export default function Compliance() {
  const [period, setPeriod] = useState(thisMonth());
  const [tab, setTab] = useState("gstr1");
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);
  const [audit, setAudit] = useState([]);
  const [chain, setChain] = useState(null);
  const [rows2b, setRows2b] = useState([]);
  const [sum2b, setSum2b] = useState(null);
  const [itc, setItc] = useState(null);
  const [busy2b, setBusy2b] = useState("");
  const [filings, setFilings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [arn, setArn] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/reports/gstr1?period=${period}`), api.get(`/reports/gstr3b?period=${period}`),
      ]);
      setGstr1(a.data); setGstr3b(b.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);

  const load2b = useCallback(async () => {
    try {
      const [rows, s, l] = await Promise.all([
        api.get(`/gst/2b?period=${period}`),
        api.get(`/gst/2b/summary?period=${period}`),
        api.get(`/gst/2b/itc-ledger?period=${period}`),
      ]);
      setRows2b(rows.data); setSum2b(s.data); setItc(l.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);

  const loadFilings = useCallback(async () => {
    try { const { data } = await api.get("/gst/gstr1/filings"); setFilings(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);

  const reconcile2b = useCallback(async () => {
    setBusy2b("recon");
    try {
      await api.post(`/gst/2b/reconcile?period=${period}`);
      await load2b();
      toast.success("2B reconciliation complete");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy2b(""); }
  }, [period, load2b]);

  const pull2b = useCallback(async () => {
    setBusy2b("pull");
    try {
      const { data } = await api.post(`/gst/2b/pull?period=${period}`);
      toast.success(`${data.imported} portal lines pulled (simulated)`);
      await reconcile2b();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy2b(""); }
  }, [period, reconcile2b]);

  const upload2b = useCallback(async (event, fileRef) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy2b("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/gst/2b/upload?period=${period}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} lines imported from ${file.name}`);
      await reconcile2b();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy2b(""); if (fileRef?.current) fileRef.current.value = ""; }
  }, [period, reconcile2b]);

  const lockItc = useCallback(async () => {
    try {
      const { data } = await api.post(`/gst/2b/itc-ledger/lock?period=${period}`);
      toast.success(`ITC ledger locked · closing ${money(data.closing)}`);
      await load2b();
    } catch (e) { toast.error(apiError(e)); }
  }, [period, load2b]);

  const previewJson = useCallback(async () => {
    try {
      const { data } = await api.get(`/gst/gstr1/preview?period=${period}`);
      setPreview(data);
      toast.success(`Schema ${data.schema_version} ready · FP ${data.fp}`);
    } catch (e) { toast.error(apiError(e)); }
  }, [period]);

  const dl = useCallback(async (kind, section) => {
    try {
      if (kind === "json")
        await downloadApiFile(`/gst/gstr1/json?period=${period}`, `GSTR1_${period}.json`);
      else
        await downloadApiFile(`/gst/gstr1/csv?period=${period}&section=${section}`, `GSTR1_${section}_${period}.csv`);
    } catch (e) { toast.error(e.message); }
  }, [period]);

  const markFiled = useCallback(async (returnType) => {
    try {
      const { data } = await api.post("/gst/gstr1/file", {
        period, return_type: returnType, arn: arn.trim() || null,
      });
      toast.success(`${returnType} logged · ARN ${data.arn}`);
      setArn("");
      await loadFilings();
    } catch (e) { toast.error(apiError(e)); }
  }, [period, arn, loadFilings]);

  useEffect(() => {
    setPreview(null);
    load();
    load2b();
  }, [load, load2b]);

  useEffect(() => {
    api.get("/audit-trail?limit=60").then((r) => setAudit(r.data)).catch((e) => console.warn("audit trail", e));
    api.get("/audit-trail/verify").then((r) => setChain(r.data)).catch((e) => console.warn("audit verify", e));
    loadFilings();
  }, [loadFilings]);

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <PageHeader title="GST Returns Studio" subtitle="GSTR-1 → 2B recon → GSTR-3B → file. Computed from posted vouchers, reviewed by a human.">
        <Input type="month" className="h-8 w-40 text-sm" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="gst-period-input" />
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-returns-btn">Recompute</Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 border border-border p-3 text-xs">
        <ShieldCheck size={16} className={chain?.valid ? "text-success" : "text-destructive"} />
        <span className="font-medium">Audit trail (MCA Rule 3(1))</span>
        <span className="text-muted-foreground" data-testid="audit-chain-status">
          {chain ? `${chain.records} hash-chained records · ${chain.valid ? "chain intact" : `broken at #${chain.broken_at}`}` : "verifying…"}
        </span>
        {chain?.head_hash ? <span className="num ml-auto text-[10px] text-muted-foreground">head {chain.head_hash.slice(0, 16)}…</span> : null}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="compliance-tabs">
          <TabsTrigger value="gstr1" data-testid="tab-gstr1">GSTR-1</TabsTrigger>
          <TabsTrigger value="gstr3b" data-testid="tab-gstr3b">GSTR-3B</TabsTrigger>
          <TabsTrigger value="gstr2b" data-testid="tab-gstr2b">GSTR-2B recon</TabsTrigger>
          <TabsTrigger value="itc" data-testid="tab-itc">ITC ledger</TabsTrigger>
          <TabsTrigger value="filings" data-testid="tab-filings">Filing log</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "gstr1" && gstr1 && (
        <Gstr1Tab gstr1={gstr1} preview={preview} arn={arn} setArn={setArn}
          onDownload={dl} onPreview={previewJson} onFile={markFiled} />
      )}
      {tab === "gstr3b" && gstr3b && <Gstr3bTab gstr3b={gstr3b} />}
      {tab === "gstr2b" && (
        <Gstr2bTab rows2b={rows2b} sum2b={sum2b} busy2b={busy2b}
          onPull={pull2b} onUpload={upload2b} onReconcile={reconcile2b} />
      )}
      {tab === "itc" && <ItcTab itc={itc} onLock={lockItc} />}
      {tab === "filings" && (
        <FilingsTab period={period} filings={filings} arn={arn} setArn={setArn} onFile={markFiled} />
      )}
      {tab === "audit" && <AuditTab audit={audit} />}
    </div>
  );
}
